import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { searchHistoryApi } from '../../utils/searchHistoryApi';
import { SearchHistoryItem } from '../../utils/searchHistory';

// Modern shadcn components
import { AppSidebar } from '@/components/app-sidebar';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useComingSoon } from '@/components/coming-soon-modal';

// Icons
import {
  TrendingUpIcon, TrendingDownIcon, DollarSignIcon, PackageIcon, 
  BarChart3Icon, UsersIcon, LeafIcon, FlaskConicalIcon, AlertTriangleIcon,
  CheckCircleIcon, XCircleIcon, InfoIcon, Beaker, Download,
  ShareIcon, LayoutDashboardIcon, BookmarkIcon, BookmarkCheckIcon,
  ClockIcon
} from 'lucide-react';

// Enhanced Charts
import { FormulationComparisonChart } from '@/polymet/components/enhanced-charts';

// SpyGlass Brand Icon - consistent with main app
const SpyglassBrandIcon = () => (
  <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="12" height="12" rx="2" stroke="#4F46E5" strokeWidth="2" fill="none"/> {/* Indigo-600 (Top-Left Outlined Square) */}
    {/* Sky-500 (Top-Right Diamond - Rotated Square) */}
    <rect x="18" y="4" width="12" height="12" rx="2" fill="#0EA5E9" transform="rotate(45 24 10)"/> 
    {/* Blue-500 (Bottom-Left Triangle - Pointing Up) */}
    <path d="M4 30 L16 30 L10 18 Z" fill="#3B82F6"/> 
    <circle cx="24" cy="24" r="6" fill="#A5B4FC"/>                        {/* Indigo-300 (Bottom-Right Filled Circle) */}
  </svg>
);

// Loading component
import CircularProgress from '@mui/material/CircularProgress';

const loadingMessages = [
  "Analyzing product formulation data...",
  "Processing market intelligence...",
  "Evaluating supply chain opportunities...",
  "Calculating optimization metrics...",
  "Finalizing insights and recommendations..."
];

export default function StreamlinedProductDashboard() {
  const { taskId } = useParams<{ taskId: string }>();
  const { token: authToken } = useAuth();
  const [taskStatus, setTaskStatus] = useState<string | null>(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState<number>(0);
  const [taskResult, setTaskResult] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showInfoCard, setShowInfoCard] = useState<boolean>(false);
  const [isSavedToDashboard, setIsSavedToDashboard] = useState<boolean>(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [viewMode, setViewMode] = useState<'summary' | 'detailed'>('summary');
  const lastScrollY = useRef(0);
  const { showComingSoon } = useComingSoon();

  const baseUrl = 'https://credible-definitely-doberman.ngrok-free.app';
  const stateName = 'food_reformulation';

  const fetchStatus = useCallback(async () => {
    if (!taskId) {
      setError('Task ID is missing.');
      setLoading(false);
      return null;
    }
    const resultPollUrl = `${baseUrl}/api/${stateName}/task/result/${taskId}`;

    const headers: HeadersInit = { 'ngrok-skip-browser-warning': 'true' };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    try {
      const response = await fetch(resultPollUrl, { headers });
      if (response.headers.get('content-type')?.includes('text/html')) {
        setError('Failed to connect to API. Check ngrok.');
        setLoading(false);
        return null;
      }
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 400 && data.detail) {
          const detailLowerCase = data.detail.toLowerCase();
          if (detailLowerCase.includes("current status: processing")) {
            setTaskStatus("Processing analysis...");
            setError(null);
            setLoading(true);
            return "PROCESSING";
          } else {
            setError(data.detail);
            setTaskStatus("Task Failed");
            setLoading(false);
            return null;
          }
        } else {
          const message = data.detail || data.message || `API error ${response.status}`;
          setError(message);
          setLoading(false);
          return null;
        }
      }

      const currentStatus = data.status || data.task_status;
      const currentStatusUpperCase = currentStatus ? currentStatus.toUpperCase() : "";

      if ((data.result && Object.keys(data.result).length > 0) || currentStatusUpperCase === 'COMPLETED') {
        setTaskStatus('Completed');
        const resultData = data.result || data;
        setTaskResult(resultData);
        setLoading(false);
        setError(null);
        
        // Check for generated images
        console.log('📊 Analysis completed! Checking for generated images...');
        const hasProductImage = resultData.product_image || resultData.product_data?.product_image;
        const hasFormulationImages = resultData.formulations?.some((f: any) => f.image_url || f.product_image);
        const hasRecommendationImages = resultData.recommendations?.some((r: any) => r.image_url || r.product_image);
        
        if (hasProductImage || hasFormulationImages || hasRecommendationImages) {
          console.log('🎨 ✅ IMAGES DETECTED in API response:');
          if (hasProductImage) console.log('  - Product image found');
          if (hasFormulationImages) console.log('  - Formulation images found');
          if (hasRecommendationImages) console.log('  - Recommendation images found');
        } else {
          console.log('📝 No images found in API response (text-only analysis)');
        }
        
        // Save completed analysis to search history with comprehensive data
        if (taskId && resultData) {
          const costReduction = resultData.formulations?.[0]?.cost_reduction_pct || 0;
          const marketValue = resultData.market_opportunity?.potential_sales_usd || 0;
          const consumerInterest = resultData.market_opportunity?.consumer_interest || 0;
          const sustainabilityScore = resultData.supply_availability?.sustainability_score || 0;
          
          // Get the real product name and objective from API response
          const apiProductName = resultData.input_params?.product_name;
          const apiObjective = resultData.input_params?.reformulation_objective;
          
          // First ensure the search result exists with correct product details
          const existingResult = await searchHistoryApi.getSearchResult(taskId);
          if (!existingResult && apiProductName) {
            await searchHistoryApi.addSearchResult(taskId, apiProductName, apiObjective || 'Analysis');
          } else if (existingResult && apiProductName && existingResult.productName !== apiProductName) {
            // Update with correct product name if it was wrong
            await searchHistoryApi.addSearchResult(taskId, apiProductName, apiObjective || existingResult.objective);
          }
          
          // Store complete analysis data for future reference
          await searchHistoryApi.updateSearchResult(taskId, 'completed', {
            costReduction,
            marketValue,
            consumerInterest,
            sustainabilityScore,
            formulations: resultData.formulations || [],
            evaluations: resultData.evaluations || [],
            recommendations: resultData.recommendations || [],
            marketInformation: resultData.market_information || {},
            supplyChainData: resultData.supply_availability || {}
          });
          
          setIsSavedToDashboard(true); // Update button state
        }
        
        return null;
      } else if (currentStatusUpperCase === 'PROCESSING' || currentStatusUpperCase === 'PENDING') {
        setTaskStatus("Analyzing product data...");
        setError(null);
        setLoading(true);
        
        // Add to search history as processing if we have input params from API  
        if (taskId && data.input_params && data.input_params.product_name) {
          const productName = data.input_params.product_name.trim();
          const objective = (data.input_params.reformulation_objective || 'Analysis').trim();
          
          // Only add/update if product name is meaningful (not fallback values)
          if (productName && productName !== 'Product Analysis' && productName !== 'Unknown Product') {
            const existingResult = await searchHistoryApi.getSearchResult(taskId);
            if (!existingResult) {
              await searchHistoryApi.addSearchResult(taskId, productName, objective);
            } else if (existingResult.productName !== productName) {
              // Update with real product name if current one is placeholder
              await searchHistoryApi.addSearchResult(taskId, productName, objective || existingResult.objective);
            }
          }
        }
        
        return currentStatusUpperCase;
              } else {
          const errorMessage = data.detail || data.error_message || 'Task failed.';
          setError(errorMessage);
          setTaskStatus('Task Failed');
          setLoading(false);
          
          // Mark as failed in search history
          if (taskId) {
            await searchHistoryApi.updateSearchResult(taskId, 'failed');
          }
          
          return null;
        }
    } catch (err: any) {
      setError(err.message || 'Polling failed.');
      setTaskStatus('Error polling status.');
      setLoading(false);
      return null;
    }
  }, [taskId, baseUrl, stateName, authToken]);

  useEffect(() => {
    let timerId: NodeJS.Timeout | null = null;
    if (loading && !taskResult) {
      timerId = setTimeout(() => setShowInfoCard(true), 2000);
    } else {
      setShowInfoCard(false);
    }
    return () => { if (timerId) clearTimeout(timerId); };
  }, [loading, taskResult]);

  useEffect(() => {
    if (loading && !taskResult) {
      const intervalId = setInterval(() => {
        setCurrentMessageIndex(prevIndex => (prevIndex + 1) % loadingMessages.length);
      }, 3000);
      return () => clearInterval(intervalId);
    }
  }, [loading, taskResult]);

    useEffect(() => {
    if (!taskId) {
      setError('No Task ID provided.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setTaskResult(null);

        // Add to search history from URL parameters (for immediate tracking of new analyses)
    const urlParams = new URLSearchParams(window.location.search);
    const urlProductName = urlParams.get('product_name');
    const urlObjective = urlParams.get('reformulation_objective');
    
    // Only add if we have real product details from URL and it's not already tracked
    if (taskId && urlProductName && urlProductName.trim() && urlProductName !== 'Product Analysis') {
      (async () => {
        const existingResult = await searchHistoryApi.getSearchResult(taskId);
        if (!existingResult) {
          await searchHistoryApi.addSearchResult(taskId, urlProductName.trim(), urlObjective?.trim() || 'Analysis');
        }
      })();
    }

    // Smart header scroll behavior
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        // Scrolling down & past threshold - hide header
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY.current) {
        // Scrolling up - show header
        setIsHeaderVisible(true);
      }
      
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // FIRST: Check if we have saved analysis data in Supabase
    const loadSavedData = async () => {
      try {
        const savedResult = await searchHistoryApi.getSearchResult(taskId);
        
        if (savedResult && savedResult.status === 'completed' && savedResult.formulations) {
          // We have complete saved data - use it instead of calling API
          console.log('Loading saved analysis data from Supabase for task:', taskId);
          
          const resultData = {
            input_params: {
              product_name: savedResult.productName,
              reformulation_objective: savedResult.objective
            },
            formulations: savedResult.formulations,
            evaluations: savedResult.evaluations,
            recommendations: savedResult.recommendations,
            market_information: savedResult.marketInformation,
            supply_chain_data: savedResult.supplyChainData,
            market_opportunity: {
              potential_sales_usd: savedResult.marketValue,
              consumer_interest: savedResult.consumerInterest
            },
            supply_availability: {
              sustainability_score: savedResult.sustainabilityScore
            }
          };
          
          setTaskResult(resultData);
          setTaskStatus('Completed');
          setLoading(false);
          setError(null);
          return true; // Indicate we loaded saved data
        }
        
        return false; // No saved data found
      } catch (error) {
        console.error('Error loading saved data:', error);
        return false; // Fall back to API call
      }
    };

    // Load saved data first, then fall back to API if needed
    loadSavedData().then(hasSavedData => {
      if (!hasSavedData) {
        // No saved data - proceed with API call
        console.log('No saved data found, fetching from API for task:', taskId);
        
        fetchStatus().then(initialStatus => {
          if (initialStatus === "PROCESSING" || initialStatus === "PENDING") {
            const intervalId = setInterval(async () => {
              const currentPollingStatus = await fetchStatus();
              if (!currentPollingStatus || !["PROCESSING", "PENDING"].includes(currentPollingStatus)) {
                clearInterval(intervalId);
              }
            }, 5000);
            return () => {
              clearInterval(intervalId);
              window.removeEventListener('scroll', handleScroll);
            };
          } else {
            setLoading(false); 
          }
        });
      }
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [taskId, fetchStatus]);

  const navigate = useNavigate();

  // Memoized data processing - MUST be before any conditional returns to follow Rules of Hooks
  const processedData = useMemo(() => {
    const inputParams = taskResult?.input_params || { product_name: 'Product Analysis', reformulation_objective: 'Optimization' };
    const currentProductData = taskResult?.product_data || {};
    const currentFormulations = taskResult?.formulations || [];
    const currentEvaluations = taskResult?.evaluations || [];
    const currentRecommendations = taskResult?.recommendations || [];
    const marketInformation = taskResult?.market_information || {};
    const marketOpportunity = taskResult?.market_opportunity || {};
    const targetConsumer = taskResult?.target_consumer || {};
    const supplyAvailability = taskResult?.supply_availability || {};

    const processedMarketOpportunity = {
      costReduction: currentFormulations?.[0]?.cost_reduction_pct ?? 0,
      marketValue: marketOpportunity?.potential_sales_usd || 0,
      consumerInterest: marketOpportunity?.consumer_interest || 0,
      growthRate: marketOpportunity?.growth_rate || 0,
      marketSize: marketOpportunity?.market_size || 0,
      competitiveAdvantage: marketOpportunity?.competitive_advantage || '',
      pricingStrategy: marketOpportunity?.pricing_strategy || '',
    };

    const keyInsights = currentRecommendations.map((rec: any) => rec.rationale).filter(Boolean);

    return {
      inputParams,
      currentProductData,
      currentFormulations,
      currentEvaluations,
      currentRecommendations,
      marketInformation,
      marketOpportunity: processedMarketOpportunity,
      targetConsumer,
      supplyAvailability,
      keyInsights
    };
  }, [taskResult]);

  const { 
    inputParams, 
    currentProductData, 
    currentFormulations, 
    currentEvaluations,
    currentRecommendations,
    marketInformation,
    marketOpportunity,
    targetConsumer,
    supplyAvailability,
    keyInsights
  } = processedData;

  // Check if current task is saved to dashboard
  const checkSaveStatus = useCallback(async () => {
    if (taskId) {
      const savedResult = await searchHistoryApi.getSearchResult(taskId);
      setIsSavedToDashboard(!!savedResult);
    }
  }, [taskId]);

  // Manual save to dashboard function
  const handleSaveToDashboard = async () => {
    if (!taskId || !taskResult) return;

    const productName = inputParams?.product_name || 'Unknown Product';
    const objective = inputParams?.reformulation_objective || 'Analysis';
    
    // Check if already exists, if not add it
    const existingResult = await searchHistoryApi.getSearchResult(taskId);
    if (!existingResult) {
      await searchHistoryApi.addSearchResult(taskId, productName, objective);
    }
    
    // Update with complete data
    const costReduction = taskResult.formulations?.[0]?.cost_reduction_pct || 0;
    const marketValue = taskResult.market_opportunity?.potential_sales_usd || 0;
    const consumerInterest = taskResult.market_opportunity?.consumer_interest || 0;
    const sustainabilityScore = taskResult.supply_availability?.sustainability_score || 0;
    
    await searchHistoryApi.updateSearchResult(taskId, 'completed', {
      costReduction,
      marketValue,
      consumerInterest,
      sustainabilityScore,
      formulations: taskResult.formulations || [],
      evaluations: taskResult.evaluations || [],
      recommendations: taskResult.recommendations || [],
      marketInformation: taskResult.market_information || {},
      supplyChainData: taskResult.supply_availability || {}
    });
    
    setIsSavedToDashboard(true);
  };

  // Handle share button click
  const handleShare = () => {
    showComingSoon('Share Analysis', 'Share your analysis reports with team members, export links, and collaborate on product development insights.');
  };

  // Handle export button click
  const handleExport = () => {
    showComingSoon('Export Analysis', 'Export your analysis data in multiple formats including PDF reports, Excel spreadsheets, and JSON data for further processing.');
  };

  // Check save status when component mounts or taskResult changes
  useEffect(() => {
    checkSaveStatus();
  }, [checkSaveStatus, taskResult]);

  // Handle section navigation from sidebar
  useEffect(() => {
    const handleNavigateToSection = (event: CustomEvent) => {
      const { sectionId } = event.detail;
      
      // Switch to detailed view first
      setViewMode('detailed');
      
      // Wait for view to switch, then scroll to section
      setTimeout(() => {
        if (sectionId === 'overview') {
          // For overview, scroll to the very top
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        } else {
          // Find the target section and scroll to it
          const element = document.getElementById(sectionId);
          if (element) {
            const headerHeight = 80; // Account for fixed header
            const elementPosition = element.offsetTop - headerHeight;
            window.scrollTo({
              top: elementPosition,
              behavior: 'smooth'
            });
          }
        }
      }, 100); // Small delay to ensure DOM update
    };

    // Add event listener for section navigation
    window.addEventListener('navigateToSection', handleNavigateToSection as EventListener);
    
    return () => {
      window.removeEventListener('navigateToSection', handleNavigateToSection as EventListener);
    };
  }, []);

  // Loading State
  if (loading && !taskResult) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/" className="flex items-center gap-2">
                    <SpyglassBrandIcon />
                    <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      SpyGlass AI
                    </span>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbPage>Analysis in Progress</BreadcrumbPage>
              </BreadcrumbList>
            </Breadcrumb>
          </header>
          <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
            <div className="min-h-[100vh] flex-1 rounded-xl bg-muted/50 p-8">
              <div className="flex flex-col items-center justify-center h-full">
                <div className="flex flex-col items-center space-y-6 text-center">
                  <div className="relative">
                    <CircularProgress size={60} thickness={4} />
                    <FlaskConicalIcon className="absolute inset-0 m-auto h-6 w-6 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h1 className="text-2xl font-semibold tracking-tight">
                      {loadingMessages[currentMessageIndex]}
                    </h1>
                    <p className="text-muted-foreground">
                      Our AI is processing your product analysis. This typically takes 3-5 minutes.
                    </p>
                  </div>
                  <Progress value={33} className="w-[300px]" />
                  
                  {showInfoCard && (
                    <Card className="w-full max-w-md animate-in slide-in-from-bottom-2">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <InfoIcon className="h-5 w-5 text-blue-500" />
                          Analysis in Progress
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm text-muted-foreground">
                          <p>✓ Market data collection</p>
                          <p>✓ Ingredient analysis</p>
                          <p className="flex items-center gap-2">
                            <CircularProgress size={12} />
                            Supply chain optimization
                          </p>
                          <p>• Formulation recommendations</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-4 w-full"
                          onClick={() => navigate('/')}
                        >
                          Cancel and return
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  // Error State
  if (error && !taskResult) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/" className="flex items-center gap-2">
                    <SpyglassBrandIcon />
                    <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      SpyGlass AI
                    </span>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbPage>Error</BreadcrumbPage>
              </BreadcrumbList>
            </Breadcrumb>
          </header>
          <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
            <div className="min-h-[100vh] flex-1 rounded-xl bg-muted/50 p-8">
              <div className="flex flex-col items-center justify-center h-full">
                <Alert className="max-w-md" variant="destructive">
                  <XCircleIcon className="h-4 w-4" />
                  <AlertTitle>Analysis Failed</AlertTitle>
                  <AlertDescription className="mt-2">
                    {error}
                  </AlertDescription>
                </Alert>
                <Button 
                  className="mt-4"
                  onClick={() => navigate('/')}
                >
                  Return to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  // Main Dashboard
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className={`flex h-16 shrink-0 items-center gap-2 border-b px-4 sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-transform duration-300 ${isHeaderVisible ? 'translate-y-0' : '-translate-y-full'}`}>
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/" className="flex items-center gap-2">
                  <SpyglassBrandIcon />
                  <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    SpyGlass AI
                  </span>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/reports">Reports</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbPage>{inputParams.product_name}</BreadcrumbPage>
            </BreadcrumbList>
          </Breadcrumb>
                    <div className="ml-auto flex items-center gap-2">
            {/* View Toggle */}
            <div className="flex items-center border rounded-lg p-1 bg-muted/30">
              <Button
                variant={viewMode === 'summary' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('summary')}
                className="h-7 px-3 text-xs"
              >
                Summary
              </Button>
              <Button
                variant={viewMode === 'detailed' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('detailed')}
                className="h-7 px-3 text-xs"
              >
                Detailed
              </Button>
            </div>
            <Separator orientation="vertical" className="h-6" />
            <Button variant="outline" size="sm" onClick={handleShare}>
              <ShareIcon className="h-4 w-4 mr-2" />
              Share
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSaveToDashboard}
              disabled={isSavedToDashboard}
              className={isSavedToDashboard ? "text-green-600 border-green-200 bg-green-50" : ""}
            >
              {isSavedToDashboard ? (
                <>
                  <BookmarkCheckIcon className="h-4 w-4 mr-2" />
                  Saved
                </>
              ) : (
                <>
                  <BookmarkIcon className="h-4 w-4 mr-2" />
                  Save to Dashboard
                </>
              )}
            </Button>
            </div>
        </header>

        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <div className="min-h-[100vh] flex-1 rounded-xl bg-muted/50 p-4 md:p-6">
            
            {viewMode === 'summary' ? (
              /* Summary View - Single Page, Non-Scrollable */
              <div className="h-[calc(100vh-8rem)] overflow-hidden">
                {/* Header Section */}
                <div className="flex items-start justify-between gap-6 mb-6">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold tracking-tight">{inputParams.product_name}</h1>
                    <p className="text-muted-foreground mt-1">
                      {inputParams.reformulation_objective}
                    </p>
                    <Badge variant="secondary" className="mt-2">
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      Analysis Complete
                    </Badge>
                  </div>
                  
                  {/* Product Image */}
                  <div className="w-48 h-32 bg-muted/50 rounded-lg border-2 border-dashed border-muted-foreground/20 flex items-center justify-center overflow-hidden">
                    {currentProductData?.product_image || taskResult?.product_image ? (
                      <img
                        src={currentProductData?.product_image || taskResult?.product_image}
                        alt={inputParams.product_name}
                        className="w-full h-full object-cover rounded-lg"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                          if (placeholder) {
                            placeholder.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className="flex flex-col items-center justify-center text-center p-2" style={{ display: currentProductData?.product_image || taskResult?.product_image ? 'none' : 'flex' }}>
                      <PackageIcon className="h-6 w-6 text-muted-foreground/40 mb-1" />
                      <span className="text-xs text-muted-foreground/60">Product Image</span>
                    </div>
                  </div>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-12 gap-4 h-[calc(100%-12rem)]">
                  
                  {/* Left Column - Key Metrics */}
                  <div className="col-span-4 space-y-4">
                    {/* Cost Reduction */}
                    <Card className="h-fit">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <DollarSignIcon className="h-5 w-5 text-green-500" />
                          Cost Reduction
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-green-600">{marketOpportunity.costReduction.toFixed(1)}%</div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {marketOpportunity.costReduction > 20 ? 'Significant savings opportunity' : 'Moderate cost optimization'}
                        </p>
                      </CardContent>
                    </Card>

                    {/* Market Opportunity */}
                    <Card className="h-fit">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <BarChart3Icon className="h-5 w-5 text-blue-500" />
                          Market Opportunity
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-blue-600">
                          ${(marketOpportunity.marketValue / 1000000).toFixed(1)}M
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Potential revenue with {marketOpportunity.growthRate.toFixed(1)}% growth
                        </p>
                      </CardContent>
                    </Card>

                    {/* Consumer Interest */}
                    <Card className="h-fit">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <UsersIcon className="h-5 w-5 text-purple-500" />
                          Consumer Interest
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-3xl font-bold text-purple-600">{marketOpportunity.consumerInterest}/10</div>
                        <Progress value={marketOpportunity.consumerInterest * 10} className="mt-2" />
                        <p className="text-sm text-muted-foreground mt-2">Consumer acceptance rating</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Center Column - Market Intelligence & Supply Chain */}
                  <div className="col-span-4 space-y-4">
                    {/* Market Intelligence */}
                    <Card className="h-fit">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <TrendingUpIcon className="h-5 w-5 text-indigo-500" />
                          Market Intelligence
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3">
                          {marketInformation?.avg_price_usd && (
                            <div className="text-center p-2 bg-indigo-50 rounded">
                              <div className="text-lg font-bold text-indigo-600">${marketInformation.avg_price_usd}</div>
                              <div className="text-xs text-indigo-800">Avg Price</div>
                            </div>
                          )}
                          {marketInformation?.sales_volume_usd && (
                            <div className="text-center p-2 bg-indigo-50 rounded">
                              <div className="text-lg font-bold text-indigo-600">${(marketInformation.sales_volume_usd / 1000000000).toFixed(1)}B</div>
                              <div className="text-xs text-indigo-800">Sales Volume</div>
                            </div>
                          )}
                        </div>
                        {marketOpportunity?.competitiveAdvantage && (
                          <div className="mt-3 p-2 bg-green-50 rounded border border-green-200">
                            <p className="text-xs text-green-800 leading-relaxed line-clamp-3">{marketOpportunity.competitiveAdvantage}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Supply Chain Insights */}
                    <Card className="h-fit">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <PackageIcon className="h-5 w-5 text-orange-500" />
                          Supply Chain Insights
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="text-center p-2 bg-orange-50 rounded">
                            <div className="text-lg font-bold text-orange-600">{supplyAvailability?.supply_risk || 'N/A'}/10</div>
                            <div className="text-xs text-orange-800">Supply Risk</div>
                          </div>
                          <div className="text-center p-2 bg-green-50 rounded">
                            <div className="text-lg font-bold text-green-600">{supplyAvailability?.sustainability_score || 'N/A'}/10</div>
                            <div className="text-xs text-green-800">Sustainability</div>
                          </div>
                        </div>
                        {supplyAvailability?.availability && (
                          <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                            <p className="text-xs text-blue-800 leading-relaxed line-clamp-3">{supplyAvailability.availability}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Right Column - Product Recommendations */}
                  <div className="col-span-4 space-y-4">
                    <Card className="h-fit">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
                          Top Formulation
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {currentFormulations.length > 0 && (
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{currentFormulations[0]?.objective || 'Formulation 1'}</span>
                              <Badge variant="secondary">{currentFormulations[0]?.cost_reduction_pct?.toFixed(1) || 0}% cost reduction</Badge>
                            </div>
                            
                            {/* Processing Method */}
                            {currentFormulations[0]?.processing_method && (
                              <div className="p-3 bg-emerald-50 rounded border border-emerald-200">
                                <h5 className="font-medium text-emerald-800 text-sm mb-1">Suggested Processing Method</h5>
                                <p className="text-xs text-emerald-700 leading-relaxed line-clamp-4">{currentFormulations[0].processing_method}</p>
                              </div>
                            )}

                            {/* Key Nutrition Improvements */}
                            {currentFormulations[0]?.nutrition_improvement && currentFormulations[0].nutrition_improvement.length > 0 && (
                              <div className="space-y-2">
                                <h5 className="font-medium text-sm">Key Improvements</h5>
                                <div className="space-y-1">
                                  {currentFormulations[0].nutrition_improvement.slice(0, 2).map((item: any, idx: number) => (
                                    <div key={idx} className="flex justify-between items-center p-2 bg-muted/50 rounded text-xs">
                                      <span className="font-medium">{item.nutrient}</span>
                                      <span className="text-green-600 font-bold">{item.improvement}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Additional Recommendations */}
                    {currentRecommendations.length > 0 && (
                      <Card className="h-fit">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <FlaskConicalIcon className="h-5 w-5 text-violet-500" />
                            Key Recommendation
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div className="font-medium text-sm">{currentRecommendations[0]?.product_name || 'Product Recommendation'}</div>
                            {currentRecommendations[0]?.rationale && (
                              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4">{currentRecommendations[0].rationale}</p>
                            )}
                            {currentRecommendations[0]?.market_opportunity_usd && (
                              <div className="grid grid-cols-2 gap-2">
                                <div className="text-center p-2 bg-violet-50 rounded">
                                  <div className="text-sm font-bold text-violet-600">${(currentRecommendations[0].market_opportunity_usd / 1000000000).toFixed(1)}B</div>
                                  <div className="text-xs text-violet-800">Market Potential</div>
                                </div>
                                {currentRecommendations[0]?.consumer_interest && (
                                  <div className="text-center p-2 bg-violet-50 rounded">
                                    <div className="text-sm font-bold text-violet-600">{currentRecommendations[0].consumer_interest}/10</div>
                                    <div className="text-xs text-violet-800">Interest Score</div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Existing Detailed View */
              <>
            {/* Header Section */}
            <div className="mb-4">
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold tracking-tight">{inputParams.product_name}</h1>
                  <p className="text-muted-foreground mt-1">
                    {inputParams.reformulation_objective}
                  </p>
                </div>
                
                {/* Product Image */}
                <div className="flex flex-col items-end gap-2">
                  <Badge variant="secondary" className="h-8">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Analysis Complete
                  </Badge>
                  
                  <div className="w-56 h-98 bg-muted/50 rounded-lg border-2 border-dashed border-muted-foreground/20 flex items-center justify-center overflow-hidden">
                    {currentProductData?.product_image || taskResult?.product_image ? (
                      <img
                        src={currentProductData?.product_image || taskResult?.product_image}
                        alt={inputParams.product_name}
                        className="w-full h-full object-cover rounded-lg"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                          if (placeholder) {
                            placeholder.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className="flex flex-col items-center justify-center text-center p-2" style={{ display: currentProductData?.product_image || taskResult?.product_image ? 'none' : 'flex' }}>
                      <PackageIcon className="h-8 w-8 text-muted-foreground/40 mb-1" />
                      <span className="text-xs text-muted-foreground/60">Product Image</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Cost Reduction</CardTitle>
                  <DollarSignIcon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{marketOpportunity.costReduction.toFixed(1)}%</div>
                  <p className="text-xs text-muted-foreground">
                    {marketOpportunity.costReduction > 20 ? (
                      <span className="text-green-600 flex items-center">
                        <TrendingUpIcon className="h-3 w-3 mr-1" />
                        Significant savings
                      </span>
                    ) : (
                      <span className="text-yellow-600 flex items-center">
                        <TrendingDownIcon className="h-3 w-3 mr-1" />
                        Moderate savings
                      </span>
                    )}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Market Opportunity</CardTitle>
                  <BarChart3Icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    ${(marketOpportunity.marketValue / 1000000).toFixed(1)}M
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <span className="text-green-600 flex items-center">
                      <TrendingUpIcon className="h-3 w-3 mr-1" />
                      {marketOpportunity.growthRate.toFixed(1)}% growth
                    </span>
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Consumer Interest</CardTitle>
                  <UsersIcon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{marketOpportunity.consumerInterest}/10</div>
                  <Progress value={marketOpportunity.consumerInterest * 10} className="mt-2" />
                </CardContent>
              </Card>
            </div>

            {/* Main Content - Single Page Layout */}
            <div className="space-y-8">
              
              {/* Overview Section */}
              <div id="overview" className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <LayoutDashboardIcon className="h-6 w-6 text-muted-foreground" />
                  <h2 className="text-2xl font-bold tracking-tight">Dashboard Overview</h2>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                  <Card className="col-span-4">
                    <CardHeader>
                      <CardTitle>Key Insights</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {keyInsights.length > 0 ? (
                        <div className="space-y-3">
                          {keyInsights.map((insight: string, index: number) => (
                            <div key={index} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                              <p className="text-sm leading-relaxed">{insight}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No insights available</p>
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card className="col-span-3">
                    <CardHeader>
                      <CardTitle>Product Specifications</CardTitle>
                      <CardDescription>Current product details</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {currentProductData?.avg_price_usd && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Price</span>
                            <span className="text-sm font-medium">${currentProductData.avg_price_usd}</span>
                          </div>
                        )}
                        {currentProductData?.pack_size_g && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Pack Size</span>
                            <span className="text-sm font-medium">{currentProductData.pack_size_g}g</span>
                          </div>
                        )}
                        {currentProductData?.sales_volume_usd && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Sales Volume</span>
                            <span className="text-sm font-medium">
                              ${(currentProductData.sales_volume_usd / 1000000000).toFixed(1)}B
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <Separator className="my-8" />

              {/* Formulations Section */}
              <div id="product-analysis" className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <FlaskConicalIcon className="h-6 w-6 text-muted-foreground" />
                  <h2 className="text-2xl font-bold tracking-tight">Product Analysis</h2>
                </div>
                
                {currentFormulations.length > 0 ? (
                  <div className="space-y-6">
                    <FormulationComparisonChart formulations={currentFormulations} />

                    {currentFormulations.map((formulation: any, index: number) => (
                      <Card key={index} className="relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-blue-500 to-purple-500"></div>
                        <CardHeader className="pl-6">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <CardTitle className="flex items-center gap-2">
                                <Beaker className="h-5 w-5" />
                                {formulation.objective || `Formulation ${index + 1}`}
                              </CardTitle>
                              <CardDescription className="flex items-center gap-4 mt-2">
                                <Badge variant="secondary">
                                  Cost reduction: {formulation.cost_reduction_pct?.toFixed(1) || 0}%
                                </Badge>
                                <Badge variant="outline">
                                  Similarity: {formulation.similarity_score?.toFixed(1) || 0}/100
                                </Badge>
                              </CardDescription>
                            </div>
                            
                            {/* Formulation Image */}
                            <div className="w-20 h-20 bg-muted/30 rounded-lg border border-muted-foreground/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {formulation.image_url || formulation.product_image ? (
                                <img
                                  src={formulation.image_url || formulation.product_image}
                                  alt={`Formulation ${index + 1}`}
                                  className="w-full h-full object-cover rounded-lg"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (placeholder) {
                                      placeholder.style.display = 'flex';
                                    }
                                  }}
                                />
                              ) : null}
                              <div className="flex flex-col items-center justify-center text-center p-1" style={{ display: formulation.image_url || formulation.product_image ? 'none' : 'flex' }}>
                                <FlaskConicalIcon className="h-4 w-4 text-muted-foreground/40 mb-1" />
                                <span className="text-xs text-muted-foreground/60">Formula</span>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pl-6">
                          <div className="space-y-6">
                            {/* Ingredients and Replacements */}
                            <div className="grid gap-6 md:grid-cols-2">
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm text-slate-600 uppercase tracking-wide">Complete Ingredient Profile</h4>
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                  {Array.isArray(formulation.original_ingredients) ? 
                                    formulation.original_ingredients.map((ingredient: string, idx: number) => (
                                      <Badge key={idx} variant="outline" className="mr-2 mb-1 text-xs">
                                        {ingredient}
                                      </Badge>
                                    )) : null
                                  }
                                </div>
                              </div>
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm text-slate-600 uppercase tracking-wide">Ingredient Replacements</h4>
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                  {Array.isArray(formulation.replacements) ? 
                                    formulation.replacements.map((item: any, idx: number) => (
                                      <div key={idx} className="p-2 bg-slate-50 rounded text-sm">
                                        <span className="font-medium text-blue-600">
                                          {item.ingredient}
                                        </span>
                                        <span className="block text-muted-foreground text-xs mt-1">
                                          → {item.replacement}
                                        </span>
                                      </div>
                                    )) : null
                                  }
                                </div>
                              </div>
                            </div>

                            {/* Nutrition Improvements */}
                            {formulation.nutrition_improvement && (
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm text-slate-600 uppercase tracking-wide">Nutrition Improvements</h4>
                                <div className="grid gap-3 md:grid-cols-3">
                                  {formulation.nutrition_improvement.map((item: any, idx: number) => (
                                    <div key={idx} className="p-3 bg-green-50 rounded border border-green-200">
                                      <div className="font-medium text-green-800 text-sm">{item.nutrient}</div>
                                      <div className="text-green-600 font-bold">{item.improvement}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Processing Method */}
                            {formulation.processing_method && (
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm text-slate-600 uppercase tracking-wide">Processing Method</h4>
                                <div className="p-4 bg-blue-50 rounded border border-blue-200">
                                  <p className="text-sm text-blue-900 leading-relaxed">{formulation.processing_method}</p>
                                </div>
                              </div>
                            )}

                            {/* Comprehensive Evaluation */}
                            {currentEvaluations && currentEvaluations[index] && (
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm text-slate-600 uppercase tracking-wide">Sensory & Performance Evaluation</h4>
                                <div className="grid gap-4 md:grid-cols-2">
                                  <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-3">
                                      <div className="p-2 bg-muted/50 rounded text-center">
                                        <div className="text-lg font-bold">{currentEvaluations[index].sensory?.taste || 'N/A'}/10</div>
                                        <div className="text-xs text-muted-foreground">Taste</div>
                                      </div>
                                      <div className="p-2 bg-muted/50 rounded text-center">
                                        <div className="text-lg font-bold">{currentEvaluations[index].sensory?.texture || 'N/A'}/10</div>
                                        <div className="text-xs text-muted-foreground">Texture</div>
                                      </div>
                                      <div className="p-2 bg-muted/50 rounded text-center">
                                        <div className="text-lg font-bold">{currentEvaluations[index].sensory?.appearance || 'N/A'}/10</div>
                                        <div className="text-xs text-muted-foreground">Appearance</div>
                                      </div>
                                      <div className="p-2 bg-muted/50 rounded text-center">
                                        <div className="text-lg font-bold">{currentEvaluations[index].sensory?.overall || 'N/A'}/10</div>
                                        <div className="text-xs text-muted-foreground">Overall</div>
                                      </div>
                                    </div>
                                    <div className="p-2 bg-muted/50 rounded text-center">
                                      <div className="text-lg font-bold">{currentEvaluations[index].combined_score?.toFixed(1) || 'N/A'}/100</div>
                                      <div className="text-xs text-muted-foreground">Combined Score</div>
                                    </div>
                                  </div>
                                  {currentEvaluations[index].sensory?.comments && (
                                    <div className="space-y-2">
                                      <h5 className="font-medium text-sm">Sensory Comments</h5>
                                      <p className="text-sm text-muted-foreground leading-relaxed">
                                        {currentEvaluations[index].sensory.comments}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="flex items-center justify-center h-32">
                      <p className="text-muted-foreground">No formulation data available</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              <Separator className="my-8" />

              {/* Market Analysis Section */}
              <div id="market-intelligence" className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <TrendingUpIcon className="h-6 w-6 text-muted-foreground" />
                  <h2 className="text-2xl font-bold tracking-tight">Market Intelligence</h2>
                </div>
                
                <div className="space-y-6">
                  {/* Market Information */}
                  {marketInformation && Object.keys(marketInformation).length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Market Information</CardTitle>
                        <CardDescription>Current market position and product details</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-4 md:grid-cols-4">
                          {marketInformation.avg_price_usd && (
                            <div className="text-center p-3 bg-muted/50 rounded">
                              <div className="text-2xl font-bold">${marketInformation.avg_price_usd}</div>
                              <div className="text-xs text-muted-foreground">Average Price</div>
                            </div>
                          )}
                          {marketInformation.sales_volume_usd && (
                            <div className="text-center p-3 bg-muted/50 rounded">
                              <div className="text-2xl font-bold">${(marketInformation.sales_volume_usd / 1000000000).toFixed(1)}B</div>
                              <div className="text-xs text-muted-foreground">Sales Volume</div>
                            </div>
                          )}
                          {marketInformation.price_elasticity && (
                            <div className="text-center p-3 bg-muted/50 rounded">
                              <div className="text-2xl font-bold">{marketInformation.price_elasticity}</div>
                              <div className="text-xs text-muted-foreground">Price Elasticity</div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Market Opportunity */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <CardTitle>Market Opportunity</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {marketOpportunity ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="text-center p-3 bg-muted/50 rounded">
                                <div className="text-2xl font-bold">
                                  ${(marketOpportunity.marketSize / 1000000).toFixed(1)}M
                                </div>
                                <div className="text-xs text-muted-foreground">Market Size</div>
                              </div>
                              <div className="text-center p-3 bg-muted/50 rounded">
                                <div className="text-2xl font-bold">{marketOpportunity.growthRate}%</div>
                                <div className="text-xs text-muted-foreground">Growth Rate</div>
                              </div>
                              <div className="text-center p-3 bg-muted/50 rounded">
                                <div className="text-2xl font-bold">
                                  ${(marketOpportunity.marketValue / 1000000000).toFixed(1)}B
                                </div>
                                <div className="text-xs text-muted-foreground">Potential Sales</div>
                              </div>
                              <div className="text-center p-3 bg-muted/50 rounded">
                                <div className="text-2xl font-bold">{marketOpportunity.consumerInterest}/10</div>
                                <div className="text-xs text-muted-foreground">Consumer Interest</div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-muted-foreground">No market opportunity data available</p>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Consumer Feedback & Demographics</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {targetConsumer?.target_demographics?.length > 0 ? (
                          <div className="space-y-3">
                            {targetConsumer.target_demographics.map((demographic: string, index: number) => (
                              <div key={index} className="flex items-center gap-3 p-2 bg-muted/50 rounded">
                                <UsersIcon className="h-4 w-4 text-blue-500" />
                                <span className="text-sm">{demographic}</span>
                              </div>
                            ))}
                            {targetConsumer.consumer_interest && (
                              <div className="mt-4 p-3 bg-green-50 rounded border border-green-200">
                                <div className="font-medium text-green-800 text-sm">Consumer Interest Score</div>
                                <div className="text-green-600 font-bold text-lg">{targetConsumer.consumer_interest}/10</div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-muted-foreground">No demographic data available</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Competitive Advantage */}
                  {marketOpportunity?.competitiveAdvantage && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Competitive Advantage</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm leading-relaxed">{marketOpportunity.competitiveAdvantage}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Pricing Strategy */}
                  {marketOpportunity?.pricingStrategy && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Pricing Strategy</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm leading-relaxed">{marketOpportunity.pricingStrategy}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Unified Formulation Analysis - Market Analytics, Go-to-Market & Supply Chain Combined */}
                  {currentEvaluations.length > 0 && currentEvaluations.some((evaluation: any) => evaluation.market?.target_demographics || evaluation.supply || evaluation.market) && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <TrendingUpIcon className="h-5 w-5 text-muted-foreground" />
                        Comprehensive Formulation Analysis
                      </h3>
                      <div className="space-y-4">
                        {currentEvaluations.map((evaluation: any, index: number) => (
                          (evaluation.market || evaluation.supply) && (
                            <Card key={index}>
                              <CardHeader>
                                <CardTitle className="text-base">Formulation {index + 1} - Complete Analysis</CardTitle>
                                <CardDescription>{currentFormulations[index]?.objective}</CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-6">
                                  {/* Market Analytics Section */}
                                  {evaluation.market && (
                                    <div className="space-y-4">
                                      <div className="flex items-center gap-2 border-b pb-2">
                                        <TrendingUpIcon className="h-4 w-4 text-blue-500" />
                                        <h4 className="font-semibold text-blue-900">Market Analytics</h4>
                                      </div>
                                      <div className="grid gap-4 md:grid-cols-2">
                                        {/* Target Demographics */}
                                        {evaluation.market.target_demographics && (
                                          <div className="space-y-3">
                                            <h5 className="font-medium text-sm">Target Demographics</h5>
                                            <div className="space-y-2">
                                              {evaluation.market.target_demographics.map((demographic: string, demoIndex: number) => (
                                                <div key={demoIndex} className="flex items-center gap-2 p-2 bg-blue-50 rounded text-sm">
                                                  <UsersIcon className="h-4 w-4 text-blue-500" />
                                                  <span>{demographic}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {/* Competitive Advantage */}
                                        {evaluation.market.competitive_advantage && (
                                          <div className="space-y-3">
                                            <h5 className="font-medium text-sm">Competitive Advantage</h5>
                                            <div className="p-3 bg-green-50 rounded border border-green-200">
                                              <p className="text-sm text-green-800 leading-relaxed">{evaluation.market.competitive_advantage}</p>
                                            </div>
                                          </div>
                                        )}
                                        {/* Pricing Strategy */}
                                        {evaluation.market.pricing_strategy && (
                                          <div className="space-y-3 md:col-span-2">
                                            <h5 className="font-medium text-sm">Formulation-Specific Pricing Strategy</h5>
                                            <div className="p-3 bg-purple-50 rounded border border-purple-200">
                                              <p className="text-sm text-purple-800 leading-relaxed">{evaluation.market.pricing_strategy}</p>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Go-to-Market Analysis Section */}
                                  {evaluation.market && (evaluation.market.go_to_market_time || evaluation.market.competitor_response_risk || evaluation.market.scalability || evaluation.market.potential_sales_usd) && (
                                    <div className="space-y-4">
                                      <div className="flex items-center gap-2 border-b pb-2">
                                        <ClockIcon className="h-4 w-4 text-green-500" />
                                        <h4 className="font-semibold text-green-900">Go-to-Market Analysis</h4>
                                      </div>
                                      
                                      {/* Go-to-Market Metrics Grid */}
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {evaluation.market.go_to_market_time && (
                                          <div className="text-center p-3 bg-blue-50 rounded border border-blue-200">
                                            <div className="text-xl font-bold text-blue-600">{evaluation.market.go_to_market_time}</div>
                                            <div className="text-xs text-blue-800">Months to Market</div>
                                          </div>
                                        )}
                                        {evaluation.market.competitor_response_risk && (
                                          <div className="text-center p-3 bg-red-50 rounded border border-red-200">
                                            <div className="text-xl font-bold text-red-600">{evaluation.market.competitor_response_risk}/10</div>
                                            <div className="text-xs text-red-800">Competitor Risk</div>
                                          </div>
                                        )}
                                        {evaluation.market.scalability && (
                                          <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                                            <div className="text-xl font-bold text-green-600">{evaluation.market.scalability}/10</div>
                                            <div className="text-xs text-green-800">Market Scalability</div>
                                          </div>
                                        )}
                                        {evaluation.market.potential_sales_usd && (
                                          <div className="text-center p-3 bg-purple-50 rounded border border-purple-200">
                                            <div className="text-lg font-bold text-purple-600">${(evaluation.market.potential_sales_usd / 1000000000).toFixed(1)}B</div>
                                            <div className="text-xs text-purple-800">Potential Sales</div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Supply Chain Analytics Section */}
                                  {evaluation.supply && (
                                    <div className="space-y-4">
                                      <div className="flex items-center gap-2 border-b pb-2">
                                        <PackageIcon className="h-4 w-4 text-orange-500" />
                                        <h4 className="font-semibold text-orange-900">Supply Chain Analysis</h4>
                                      </div>
                                      
                                      {/* Supply Metrics Grid */}
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {evaluation.supply.supply_risk && (
                                          <div className="text-center p-3 bg-red-50 rounded border border-red-200">
                                            <div className="text-xl font-bold text-red-600">{evaluation.supply.supply_risk}/10</div>
                                            <div className="text-xs text-red-800">Supply Risk</div>
                                          </div>
                                        )}
                                        {evaluation.supply.scalability && (
                                          <div className="text-center p-3 bg-blue-50 rounded border border-blue-200">
                                            <div className="text-xl font-bold text-blue-600">{evaluation.supply.scalability}/10</div>
                                            <div className="text-xs text-blue-800">Scalability</div>
                                          </div>
                                        )}
                                        {evaluation.supply.sustainability_score && (
                                          <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                                            <div className="text-xl font-bold text-green-600">{evaluation.supply.sustainability_score}/10</div>
                                            <div className="text-xs text-green-800">Sustainability</div>
                                          </div>
                                        )}
                                        {evaluation.supply.lead_time && (
                                          <div className="text-center p-3 bg-purple-50 rounded border border-purple-200">
                                            <div className="text-xl font-bold text-purple-600">{evaluation.supply.lead_time}</div>
                                            <div className="text-xs text-purple-800">Lead Time (days)</div>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Detailed Supply Information */}
                                      <div className="grid gap-4 md:grid-cols-2">
                                        {/* Detailed Supply Availability */}
                                        {evaluation.supply.availability && (
                                          <div className="space-y-3">
                                            <h5 className="font-medium text-sm">Supply Availability</h5>
                                            <div className="p-3 bg-blue-50 rounded border border-blue-200">
                                              <p className="text-sm text-blue-900 leading-relaxed">{evaluation.supply.availability}</p>
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Sustainability Details */}
                                        {evaluation.supply.sustainability && (
                                          <div className="space-y-3">
                                            <h5 className="font-medium text-sm">Sustainability Rating</h5>
                                            <div className="p-3 bg-green-50 rounded border border-green-200">
                                              <div className="flex items-center gap-2">
                                                <LeafIcon className="h-4 w-4 text-green-600" />
                                                <span className="text-sm text-green-800 font-medium capitalize">{evaluation.supply.sustainability}</span>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Separator className="my-8" />

              {/* Supply Chain Section */}
              <div id="supply-chain" className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <PackageIcon className="h-6 w-6 text-muted-foreground" />
                  <h2 className="text-2xl font-bold tracking-tight">Supply Chain</h2>
                </div>
                
                <div className="space-y-6">
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <CardTitle>Supply Chain Metrics</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {supplyAvailability ? (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="text-center p-3 bg-muted/50 rounded">
                                <div className="text-2xl font-bold">{supplyAvailability.supply_risk || 'N/A'}/10</div>
                                <div className="text-xs text-muted-foreground">Supply Risk</div>
                              </div>
                              <div className="text-center p-3 bg-muted/50 rounded">
                                <div className="text-2xl font-bold">{supplyAvailability.scalability || 'N/A'}/10</div>
                                <div className="text-xs text-muted-foreground">Scalability</div>
                              </div>
                              <div className="text-center p-3 bg-muted/50 rounded">
                                <div className="text-2xl font-bold">{supplyAvailability.sustainability_score || 'N/A'}/10</div>
                                <div className="text-xs text-muted-foreground">Sustainability Score</div>
                              </div>
                              <div className="text-center p-3 bg-muted/50 rounded">
                                <div className="text-lg font-bold capitalize">{supplyAvailability.sustainability || 'N/A'}</div>
                                <div className="text-xs text-muted-foreground">Sustainability Rating</div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-muted-foreground">No supply chain metrics available</p>
                        )}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Risk Assessment</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {currentEvaluations?.[0]?.supply?.risk_factors ? (
                          <div className="space-y-3">
                            {currentEvaluations[0].supply.risk_factors.map((risk: string, index: number) => (
                              <div key={index} className="flex items-start gap-3 p-2 bg-yellow-50 rounded border border-yellow-200">
                                <AlertTriangleIcon className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-yellow-800">{risk}</span>
                              </div>
                            ))}
                            {currentEvaluations[0].supply?.lead_time && (
                              <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                                <div className="font-medium text-blue-800 text-sm">Lead Time</div>
                                <div className="text-blue-600 font-bold">{currentEvaluations[0].supply.lead_time} days</div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-muted-foreground">No risk factors identified</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Supply Availability Details */}
                  {supplyAvailability?.availability && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Supply Availability Details</CardTitle>
                        <CardDescription>Comprehensive availability analysis for raw materials and ingredients</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm leading-relaxed">{supplyAvailability.availability}</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              <Separator className="my-8" />

              {/* Sustainability Section */}
              <div id="sustainability" className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <LeafIcon className="h-6 w-6 text-muted-foreground" />
                  <h2 className="text-2xl font-bold tracking-tight">Sustainability</h2>
                </div>
                
                <div className="space-y-6">
                  {/* Sustainability Metrics */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <Card>
                      <CardHeader>
                        <CardTitle>Environmental Impact</CardTitle>
                        <CardDescription>Sustainability metrics and environmental benefits</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-3 bg-green-50 rounded">
                              <div className="text-2xl font-bold text-green-600">
                                {supplyAvailability?.sustainability_score || 'N/A'}/10
                              </div>
                              <div className="text-xs text-green-800">Sustainability Score</div>
                            </div>
                            <div className="text-center p-3 bg-blue-50 rounded">
                              <div className="text-lg font-bold text-blue-600 capitalize">
                                {supplyAvailability?.sustainability || 'N/A'}
                              </div>
                              <div className="text-xs text-blue-800">Rating</div>
                            </div>
                          </div>
                          <div className="p-3 bg-emerald-50 rounded border border-emerald-200">
                            <h4 className="font-medium text-emerald-800 text-sm mb-2">Environmental Benefits</h4>
                            <p className="text-sm text-emerald-700">
                              Improved formulations contribute to reduced environmental impact through sustainable ingredient sourcing and optimized supply chain efficiency.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Sustainable Sourcing</CardTitle>
                        <CardDescription>Supply chain sustainability analysis</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {currentFormulations.length > 0 && currentFormulations[0].replacements ? (
                            currentFormulations[0].replacements.slice(0, 3).map((replacement: any, index: number) => (
                              <div key={index} className="flex items-center gap-3 p-2 bg-green-50 rounded border border-green-200">
                                <LeafIcon className="h-4 w-4 text-green-600" />
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-green-800">{replacement.replacement}</div>
                                  <div className="text-xs text-green-600">Sustainable alternative</div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-muted-foreground text-sm">No sustainable sourcing data available</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>

              <Separator className="my-8" />

              {/* Product Recommendations Section */}
              {currentRecommendations.length > 0 && (
                <div id="product-recommendations" className="space-y-4">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircleIcon className="h-6 w-6 text-muted-foreground" />
                    <h2 className="text-2xl font-bold tracking-tight">Product Recommendations</h2>
                  </div>
                  
                  <div className="space-y-6">
                    {currentRecommendations.map((recommendation: any, index: number) => (
                      <Card key={index} className="relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-indigo-500 to-purple-500"></div>
                        <CardHeader className="pl-6">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <CardTitle className="flex items-center gap-2">
                                <CheckCircleIcon className="h-5 w-5 text-indigo-500" />
                                {recommendation.product_name || `Recommendation ${index + 1}`}
                              </CardTitle>
                              {recommendation.product_summary && (
                                <CardDescription className="text-sm leading-relaxed mt-2">
                                  {recommendation.product_summary}
                                </CardDescription>
                              )}
                            </div>
                            
                            {/* Recommendation Image */}
                            <div className="w-24 h-24 bg-indigo-50 rounded-lg border border-indigo-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                              {recommendation.image_url || recommendation.product_image ? (
                                <img
                                  src={recommendation.image_url || recommendation.product_image}
                                  alt={recommendation.product_name || `Recommendation ${index + 1}`}
                                  className="w-full h-full object-cover rounded-lg"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (placeholder) {
                                      placeholder.style.display = 'flex';
                                    }
                                  }}
                                />
                              ) : null}
                              <div className="flex flex-col items-center justify-center text-center p-1" style={{ display: recommendation.image_url || recommendation.product_image ? 'none' : 'flex' }}>
                                <CheckCircleIcon className="h-6 w-6 text-indigo-400 mb-1" />
                                <span className="text-xs text-indigo-600">Product</span>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pl-6">
                          <div className="space-y-6">
                            {/* Key Metrics */}
                            {recommendation.market_opportunity_usd && (
                              <div className="grid gap-4 md:grid-cols-4">
                                <div className="text-center p-3 bg-indigo-50 rounded border border-indigo-200">
                                  <div className="text-xl font-bold text-indigo-600">
                                    ${(recommendation.market_opportunity_usd / 1000000000).toFixed(1)}B
                                  </div>
                                  <div className="text-xs text-indigo-800">Market Opportunity</div>
                                </div>
                                {recommendation.consumer_interest && (
                                  <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                                    <div className="text-xl font-bold text-green-600">{recommendation.consumer_interest}/10</div>
                                    <div className="text-xs text-green-800">Consumer Interest</div>
                                  </div>
                                )}
                                {recommendation.formulation?.cost_reduction_pct && (
                                  <div className="text-center p-3 bg-blue-50 rounded border border-blue-200">
                                    <div className="text-xl font-bold text-blue-600">{recommendation.formulation.cost_reduction_pct}%</div>
                                    <div className="text-xs text-blue-800">Cost Reduction</div>
                                  </div>
                                )}
                                {recommendation.formulation?.similarity_score && (
                                  <div className="text-center p-3 bg-purple-50 rounded border border-purple-200">
                                    <div className="text-xl font-bold text-purple-600">{recommendation.formulation.similarity_score}%</div>
                                    <div className="text-xs text-purple-800">Similarity Score</div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Processing Method */}
                            {recommendation.processing_method && (
                              <div>
                                <h4 className="font-semibold text-sm text-slate-600 uppercase tracking-wide mb-3">Suggested Processing Method</h4>
                                <div className="p-4 bg-blue-50 rounded border border-blue-200">
                                  <p className="text-sm text-blue-900 leading-relaxed">{recommendation.processing_method}</p>
                                </div>
                              </div>
                            )}

                            {/* Sensory Evaluation */}
                            {recommendation.sensory_evaluation && (
                              <div>
                                <h4 className="font-semibold text-sm text-slate-600 uppercase tracking-wide mb-3">Sensory Evaluation</h4>
                                <div className="grid grid-cols-4 gap-3">
                                  <div className="text-center p-3 bg-muted/50 rounded">
                                    <div className="text-lg font-bold">{recommendation.sensory_evaluation.taste || 'N/A'}/10</div>
                                    <div className="text-xs text-muted-foreground">Taste</div>
                                  </div>
                                  <div className="text-center p-3 bg-muted/50 rounded">
                                    <div className="text-lg font-bold">{recommendation.sensory_evaluation.texture || 'N/A'}/10</div>
                                    <div className="text-xs text-muted-foreground">Texture</div>
                                  </div>
                                  <div className="text-center p-3 bg-muted/50 rounded">
                                    <div className="text-lg font-bold">{recommendation.sensory_evaluation.appearance || 'N/A'}/10</div>
                                    <div className="text-xs text-muted-foreground">Appearance</div>
                                  </div>
                                  <div className="text-center p-3 bg-muted/50 rounded">
                                    <div className="text-lg font-bold">{recommendation.sensory_evaluation.overall || 'N/A'}/10</div>
                                    <div className="text-xs text-muted-foreground">Overall</div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Supply Availability */}
                            {recommendation.supply_availability && (
                              <div>
                                <h4 className="font-semibold text-sm text-slate-600 uppercase tracking-wide mb-3">Supply Availability Analysis</h4>
                                <div className="p-4 bg-orange-50 rounded border border-orange-200">
                                  <p className="text-sm text-orange-900 leading-relaxed">{recommendation.supply_availability}</p>
                                </div>
                              </div>
                            )}

                            {/* Rationale */}
                            {recommendation.rationale && (
                              <div>
                                <h4 className="font-semibold text-sm text-slate-600 uppercase tracking-wide mb-3">Strategic Rationale</h4>
                                <div className="p-4 bg-gray-50 rounded border border-gray-200">
                                  <p className="text-sm leading-relaxed">{recommendation.rationale}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
            </div>
            </>
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
} 